import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import {
  buildLeadSmsText,
  getSmsTemplates,
  sendSmsToLead,
  type SendSmsResult,
} from '@/api/sms'
import { formatPhoneDisplay, formatPhoneInput } from '@/utils/phone'

interface Props {
  /** Лид, по которому отправляется SMS. Уйдёт в sms_outbox.lead_id для
   *  раздела «Коммуникации» в карточке. */
  leadId?: string
  /** Телефон клиента из лида. Может быть null — тогда диспетчер вводит руками. */
  clientPhone: string | null
  /** Токен ссылки на лендинг. Null в terminal-статусах — кнопка тогда disabled. */
  landingToken: string | null
  /** Опциональный хук — выполняется ДО открытия модалки. Карточка лида
   *  передаёт сюда auto-save грязного draft'а, чтобы оператор не потерял
   *  только что введённые поля при отправке SMS. */
  beforeOpen?: () => void | Promise<void>
  /** Внешняя блокировка кнопки (напр. лид ещё не взят в работу). */
  disabled?: boolean
}

/**
 * Самостоятельная кнопка «Отправить SMS с заявкой» + модалка подтверждения.
 *
 * Подключается на карточке лида (`InspectionLeadDetailPage`) одной
 * строкой: `<SendSmsToLeadButton clientPhone={lead.client_phone}
 * landingToken={lead.landing_token} />`.
 *
 * В диалоге диспетчер видит:
 * - Поле телефона, предзаполненное из лида (можно изменить — в т.ч. для
 *   отладочной отправки на собственный номер).
 * - Текст SMS, который пойдёт оператору (read-only, чтобы единый шаблон).
 * - Счётчик сегментов SMS — кириллицей UCS-2 лимит 70 симв на 1 сегмент.
 *
 * Текст по ТЗ: «осмотр авто по заявке Авито <ссылка>», префикс «Carlink:»
 * не нужен — он уже в `originator` у оператора.
 */
export function SendSmsToLeadButton({ leadId, clientPhone, landingToken, beforeOpen, disabled }: Props) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [opening, setOpening] = useState(false)
  if (!landingToken) {
    return (
      <Button
        variant="secondary"
        disabled
        title={t('leads.sms.requestDisabledTitle')}
      >
        {t('leads.sms.requestSend')}
      </Button>
    )
  }
  const handleClick = async () => {
    if (beforeOpen) {
      setOpening(true)
      try {
        await beforeOpen()
      } finally {
        setOpening(false)
      }
    }
    setOpen(true)
  }
  return (
    <>
      <Button variant="secondary" onClick={handleClick} disabled={opening || disabled}>
        {opening ? t('leads.sms.saving') : t('leads.sms.requestSend')}
      </Button>
      {open ? (
        <SendSmsDialog
          leadId={leadId}
          clientPhone={clientPhone}
          landingToken={landingToken}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  )
}

function SendSmsDialog({
  leadId,
  clientPhone,
  landingToken,
  onClose,
}: {
  leadId?: string
  clientPhone: string | null
  landingToken: string
  onClose: () => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [phone, setPhone] = useState(clientPhone ? formatPhoneDisplay(clientPhone) : '')
  const [result, setResult] = useState<SendSmsResult | null>(null)

  // Шаблон текста — из system_config (единый источник с триггерами БД). Пока
  // грузится / при ошибке — билдер сам подставит вшитый дефолт.
  const { data: templates } = useQuery({
    queryKey: ['sms-templates'],
    queryFn: getSmsTemplates,
    staleTime: 60_000,
  })
  const message = buildLeadSmsText(landingToken, templates?.lead)
  const phoneTrim = phone.trim()
  const canSend = phoneTrim.length > 0

  const mut = useMutation({
    mutationFn: async (): Promise<SendSmsResult> => {
      return sendSmsToLead({ phone: phoneTrim, message, leadId, source: 'manual' })
    },
    onSuccess: (r) => {
      setResult(r)
      // Раздел «Коммуникации» дёргает count/list по нормализованному телефону —
      // инвалидируем оба, чтобы лента/счётчик обновились мгновенно, без F5.
      if (r.ok) {
        void qc.invalidateQueries({ queryKey: ['sms-outbox-count'] })
        void qc.invalidateQueries({ queryKey: ['sms-outbox-list'] })
      }
    },
  })

  const pending = mut.isPending
  const isOk = result?.ok === true
  const segments = countSmsSegments(message)

  return (
    <Modal
      title={t('leads.sms.requestSend')}
      onClose={onClose}
      footer={
        isOk ? (
          <Button variant="primary" onClick={onClose}>
            {t('common.done')}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={pending}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setResult(null)
                mut.mutate()
              }}
              disabled={pending || !canSend}
            >
              {pending ? t('leads.sms.sending') : t('leads.sms.send')}
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
          {t('leads.sms.requestDesc')}
        </p>

        <Input
          label={t('leads.sms.phone')}
          required
          value={phone}
          onChange={(e) => setPhone(formatPhoneInput(phone, e.target.value))}
          disabled={pending || isOk}
          hint={t('leads.sms.phoneHint')}
          placeholder="+7 999 123 45 67"
          inputMode="tel"
        />

        <div>
          <div className="stryzh-input-label !inline-flex items-center gap-3 leading-none">
            <span>{t('leads.sms.messageText')}</span>
          </div>
          <div className="mt-1 rounded border border-[color:var(--stryzh-color-border)] bg-[color:var(--stryzh-color-surface-elevated)] p-3 text-sm">
            <code className="break-all font-mono">{message}</code>
          </div>
          <div className="mt-1 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
            {t('leads.sms.meta', { chars: message.length, segments })}
            {segments > 1 ? t('leads.sms.tariff', { n: segments }) : ''}
          </div>
        </div>

        {result ? <ResultBlock result={result} /> : null}
      </div>
    </Modal>
  )
}

function ResultBlock({ result }: { result: SendSmsResult }) {
  const { t } = useTranslation()
  if (result.ok) {
    return (
      <div className="rounded border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-200">
        <div className="font-medium">{t('leads.sms.queued')}</div>
        <div className="mt-1 break-all">
          {result.description}
          {result.attemptedHosts.length > 1 ? (
            <>{t('leads.sms.fallbackVia', { hosts: result.attemptedHosts.join(' → ') })}</>
          ) : null}
        </div>
      </div>
    )
  }
  return (
    <div className="rounded border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
      <div className="font-medium">
        {result.code !== null ? t('leads.sms.errCode', { code: result.code }) : t('leads.sms.err')}
      </div>
      <div className="mt-1 break-all">{result.description}</div>
      {result.reason ? <div className="mt-1">{t('leads.sms.reason', { reason: result.reason })}</div> : null}
      {result.attemptedHosts && result.attemptedHosts.length > 0 ? (
        <div className="mt-1">{t('leads.sms.hosts', { hosts: result.attemptedHosts.join(', ') })}</div>
      ) : null}
    </div>
  )
}

/**
 * Кириллица идёт в UCS-2: 70 символов на 1 сегмент, либо 67 на сегмент в
 * multipart. Латиница без extension-символов — GSM-7: 160 / 153. Простая
 * эвристика: если есть хоть один не-ASCII символ — считаем как UCS-2.
 */
function countSmsSegments(text: string): number {
  if (text.length === 0) return 0
  const isUcs2 = /[^\x00-\x7F]/.test(text)
  if (isUcs2) {
    return text.length <= 70 ? 1 : Math.ceil(text.length / 67)
  }
  return text.length <= 160 ? 1 : Math.ceil(text.length / 153)
}
